"use client";

import React from "react";
import { ResponsiveSelect } from "@/components/ui/responsive-select";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { MobileDetailModalField } from "./MobileDetailModalField";
import { useMobileDetailModalEditing } from "./useMobileDetailModalEditing";
import type { BaseEntity, SimpleFieldConfig, SimpleTableConfig } from "../types";

export interface SectionedMobileFieldGroup {
  id: string;
  label: string;
  fieldIds: string[];
}

interface SectionedMobileDetailPanelProps<T extends BaseEntity> {
  item: T;
  config: SimpleTableConfig<T>;
  onInlineEdit?: (item: T) => Promise<void>;
}

/**
 * Builds section groups from `config.mobileDetailSections` + per-field `mobileDetailSectionId`.
 * Field order within a section follows `config.fields` key order.
 */
export function buildMobileDetailSectionGroups<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
  roleFilteredFields: Record<string, SimpleFieldConfig>
): SectionedMobileFieldGroup[] {
  const mobileDetailSections = config.mobileDetailSections;
  if (!mobileDetailSections?.length) return [];

  const sectionBuckets = new Map<string, string[]>();
  for (const s of mobileDetailSections) {
    sectionBuckets.set(s.id, []);
  }

  const unsectioned: string[] = [];

  // Full superset: every editable field renders here, INCLUDING fields also
  // edited in the detail header (the panel and header intentionally overlap so
  // any field is editable from the properties panel). Only config-read-only
  // fields (`editable: false` / `isReadOnlyDerived`) and form-only fields are
  // dropped — this is the inline EDIT panel, so non-editable fields are noise.
  for (const fieldId of Object.keys(config.fields)) {
    const fieldConfig = roleFilteredFields[fieldId];
    if (
      !fieldConfig ||
      fieldConfig.formOnly ||
      fieldConfig.editable === false ||
      fieldConfig.isReadOnlyDerived
    )
      continue;

    const sid = fieldConfig.mobileDetailSectionId;
    if (sid && sectionBuckets.has(sid)) {
      sectionBuckets.get(sid)!.push(fieldId);
    } else {
      unsectioned.push(fieldId);
    }
  }

  const result: SectionedMobileFieldGroup[] = mobileDetailSections.map((s) => ({
    id: s.id,
    label: s.label,
    fieldIds: sectionBuckets.get(s.id) ?? [],
  }));

  if (unsectioned.length > 0) {
    result.push({
      id: "__unsectioned",
      label: config.mobileDetailUnsectionedLabel ?? "Other",
      fieldIds: unsectioned,
    });
  }

  return result.filter((section) => section.fieldIds.length > 0);
}

function SectionedMobileDetailPanelComponent<T extends BaseEntity>({
  item,
  config,
  onInlineEdit,
}: SectionedMobileDetailPanelProps<T>) {
  const { filterFieldsByRole, roleName } = useFieldFiltering();
  const [selectedSectionId, setSelectedSectionId] = React.useState<string>("");

  const {
    editingField,
    isEditing,
    editedItem,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  } = useMobileDetailModalEditing(item);

  const handleSave = React.useCallback(
    async (fId: string) => {
      await handleSaveField(fId, onInlineEdit);
    },
    [handleSaveField, onInlineEdit]
  );

  const roleFilteredFields = React.useMemo(
    () => filterFieldsByRole(config.fields),
    [config.fields, filterFieldsByRole]
  );

  const sections = React.useMemo(
    () => buildMobileDetailSectionGroups(config, roleFilteredFields),
    [config, roleFilteredFields]
  );

  React.useEffect(() => {
    if (sections.length === 0) return;
    if (!sections.some((s) => s.id === selectedSectionId)) {
      setSelectedSectionId(sections[0].id);
    }
  }, [sections, selectedSectionId]);

  const sectionOptions = React.useMemo(
    () => sections.map((section) => ({ value: section.id, label: section.label })),
    [sections]
  );

  const fieldsInSelectedSection = React.useMemo((): [string, SimpleFieldConfig][] => {
    const currentSection = sections.find((section) => section.id === selectedSectionId);
    if (!currentSection) return [];

    const out: [string, SimpleFieldConfig][] = [];
    for (const fieldId of currentSection.fieldIds) {
      const fieldConfig = roleFilteredFields[fieldId];
      if (fieldConfig && !fieldConfig.formOnly) {
        out.push([fieldId, fieldConfig]);
      }
    }
    return out;
  }, [sections, selectedSectionId, roleFilteredFields]);

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <ResponsiveSelect
          value={selectedSectionId || sections[0]?.id}
          onValueChange={setSelectedSectionId}
          options={sectionOptions}
          placeholder="Select section"
          className="w-full"
        />
      </div>

      {fieldsInSelectedSection.length === 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-600 dark:text-gray-400">
          No fields available for this section.
        </div>
      ) : (
        <div className="space-y-4">
          {fieldsInSelectedSection.map(([fieldId, fieldConfig]) => {
            const value = editedItem[fieldId as keyof T] ?? null;
            const isFieldEditing = editingField === fieldId;

            return (
              <MobileDetailModalField
                key={fieldId}
                fieldId={fieldId}
                fieldConfig={fieldConfig}
                value={value}
                item={item}
                editedValue={value}
                isEditing={isEditing}
                isFieldEditing={isFieldEditing}
                canEdit={Boolean(config.actions?.edit)}
                roleName={roleName ?? undefined}
                onEdit={handleFieldEdit}
                onSave={handleSave}
                onCancel={handleCancelEdit}
                onValueChange={handleFieldValueChange}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function areSectionedMobileDetailPanelPropsEqual<T extends BaseEntity>(
  prevProps: SectionedMobileDetailPanelProps<T>,
  nextProps: SectionedMobileDetailPanelProps<T>
): boolean {
  if (prevProps.item?.id !== nextProps.item?.id) return false;
  if (prevProps.config !== nextProps.config) return false;
  if (prevProps.onInlineEdit !== nextProps.onInlineEdit) return false;
  return true;
}

export const SectionedMobileDetailPanel = React.memo(
  SectionedMobileDetailPanelComponent,
  areSectionedMobileDetailPanelPropsEqual
) as typeof SectionedMobileDetailPanelComponent;
